import { isReferenceViewerHref } from '../utils/citation-helpers'
import type { Node } from 'prosemirror-model'
import type { Decoration, EditorView } from 'prosemirror-view'

/**
 * Expected attribute shape for referenceMarker nodes. The schema defines these
 * in ./schema.ts; keep in sync if attrs are renamed or added.
 */
interface ReferenceMarkerAttrs {
  citationId: string
  label: string
  viewerUrl: string
  displayName: string
}

/**
 * ProseMirror NodeView for referenceMarker nodes.
 * Renders citation markers as clickable [1], [2] badges.
 * Hover shows the source details (native title tooltip); clicking opens the
 * referenced document in a new tab.
 *
 * Reads node decorations to pick up inline-edit highlight classes — the
 * highlight plugin emits Decoration.node for each referenceMarker inside
 * the highlight range so badges visually match the surrounding highlight.
 */
export class ReferenceMarkerView {
  dom: HTMLSpanElement

  private clickHandler: (() => void) | null = null
  private highlightClass: string | null = null

  constructor(node: Node, _view: EditorView, _getPos: () => number | undefined, decorations: readonly Decoration[]) {
    this.dom = document.createElement('span')
    this.highlightClass = extractHighlightClass(decorations)
    this.applyAttrs(node)
  }

  private applyAttrs(node: Node): void {
    const { label, viewerUrl, displayName } = node.attrs as ReferenceMarkerAttrs
    const hasUrl = Boolean(viewerUrl)

    this.dom.contentEditable = 'false'
    this.dom.textContent = `[${label}]`

    const baseClass = hasUrl
      ? 'reference-marker-badge text-brand-700 hover:text-brand-800 cursor-pointer'
      : 'reference-marker-badge text-brand-neutral-400 cursor-default'
    this.dom.className = this.highlightClass ? `${baseClass} ${this.highlightClass}` : baseClass

    if (hasUrl) {
      this.dom.setAttribute('aria-label', `Reference ${label}: ${displayName}`)
      this.dom.setAttribute('title', `${displayName}`)
      this.dom.removeAttribute('aria-disabled')
      this.removeClickHandler()
      this.clickHandler = () => {
        // noopener severs window.opener; the href guard rejects non-viewer URLs
        // (node attrs are paste-injectable via parseDOM).
        if (isReferenceViewerHref(viewerUrl)) window.open(viewerUrl, '_blank', 'noopener,noreferrer')
      }
      this.dom.addEventListener('click', this.clickHandler)
    } else {
      // No viewer URL — the tooltip is just the source name; availability is conveyed
      // by the styling and aria-label.
      this.dom.setAttribute('aria-label', `Reference ${label}: ${displayName} (unavailable)`)
      this.dom.setAttribute('title', `${displayName}`)
      this.dom.setAttribute('aria-disabled', 'true')
      this.removeClickHandler()
    }
  }

  private removeClickHandler(): void {
    if (this.clickHandler) {
      this.dom.removeEventListener('click', this.clickHandler)
      this.clickHandler = null
    }
  }

  stopEvent(): boolean {
    return true
  }

  ignoreMutation(): boolean {
    return true
  }

  update(node: Node, decorations: readonly Decoration[]): boolean {
    if (node.type.name !== 'referenceMarker') return false
    this.highlightClass = extractHighlightClass(decorations)
    this.applyAttrs(node)
    return true
  }

  destroy(): void {
    this.removeClickHandler()
  }
}

function extractHighlightClass(decorations: readonly Decoration[]): string | null {
  for (const dec of decorations) {
    const specClass = (dec.spec as { class?: string } | undefined)?.class
    if (typeof specClass === 'string') return specClass
  }
  return null
}
